import React from "react"
import dayjs from 'dayjs';
import * as Yup from 'yup';
import { isNumber } from "lodash";
import { Grid } from '@mui/material';
import { Container } from './styled';
import { useRouter } from "next/router";
import { LoadingBuffer } from "../utils/Loading";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextFieldElaboration } from "../../Inputs/TextField";
import { Organograma } from "@/utils/organograma/Organograma";
import { ActionButtonElaborations } from "../../ActionButton";
import { useTipeOfFlow } from "@/services/zustand/tipo_fluxo";
import { DatePickerElaboration } from "../../Inputs/DatePicker";
import { ReutilizarElaborationDialog } from "./ReutilizarModal";
import { RadioCheckedElaboration } from "../../Inputs/RadioButton";
import { ElaborationService } from "@/services/endpoints/elaboracao";
import { DisclaimerReuseElaborationDialog } from "./disclaimerModal";
import { useSnackbarElaboration } from "@/hooks/useSnackbarElaboration";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { findingMenuItemKey } from "@/contexts/elaboracao_planos/constants";
import { RelacionamentoJsonService } from "@/services/endpoints/hierarquia";
import { addItemToNodeOrganogram } from "@/utils/organograma/addItemToNode";
import { useElaborationCardItem } from "@/services/zustand/elaboration_card_item";
import { useHierarchyPreparationOfPlans, usePreparationOfPlans } from "@/hooks/elaboracao_planos";

type InputsVision = {
  visao: any;
  is_optional?: boolean;
};

const defaultValue = {
  id: '',
  texto: '',
  data: null,
}

const SchemaVision = Yup.object().shape({
  visao: Yup.array().of(
    Yup.object().shape({
      texto: Yup.string().required('Campo obrigatório'),
      data: Yup.date().nullable().required('Campo obrigatório')
    })
  ).required('Campo obrigatório'),
});

export const VisionForm: React.FC = () => {
  const {
    watch: watchVision,
    control: controlVision,
    register: registerVision,
    setValue: setValueVision,
    handleSubmit: handleSubmitVision,
    formState: { errors: errosVision } } = useForm<InputsVision>({
      resolver: yupResolver(SchemaVision),
      defaultValues: { visao: [defaultValue] }
    });

  const { } = useFieldArray({
    control: controlVision,
    name: "visao"
  });

  const fields = watchVision('visao');
  const isOptional = watchVision('is_optional') as unknown as string;

  const router = useRouter();
  const [data, setData] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const { tipo: tipoFluxo, planId } = useTipeOfFlow();
  const [reutilizar, setReutilizar] = React.useState(false);
  const [disabeSubmit, setDisableSubmit] = React.useState(false);
  const [openDisclaimer, setOpenDisclaimer] = React.useState(false);
  const { toggleErrorSnackbar, toggleSuccessSnackbar } = useSnackbarElaboration();
  const zustandSelectedCardItem = useElaborationCardItem(state => state.cardItem);
  const { VisionElaboration, GetVisionElaboration, PostProgressCode } = React.useMemo(() => new ElaborationService(), []);
  const { itemsMenu, selectedItemsMenu, completCode, handleFetchProgressCodePlanId, setNavigable, setDefaultItemMenuId } = usePreparationOfPlans();

  function getMessageErrorByNameRef(json: any, title: any) {
    const foundObject = json?.visao?.find((item: any) => item && item[title]);
    return foundObject ? foundObject[title].message : null;
  }

  React.useEffect(() => {
    if (
      (router.query.id && router.pathname.split('/').includes('elaboracao_planos')) ||
      router.pathname.includes('aprovacao_planos')
    )
      setLoading(true)
    GetVisionElaboration(router.query.id).then((res: any) => {
      if (res.data.length > 0) {
        const data = res.data.map((item: any) => {
          return {
            texto: item.texto,
            data: dayjs(item.data),
            id: item.id
          }
        })
        setValueVision('visao', data)
      }
    }).finally(() => setLoading(false))
  }, [GetVisionElaboration, router.pathname, router.query.id, setValueVision]);

  React.useEffect(() => {
    if (planId && router.pathname.split('/').includes("elaboracao_planos") && tipoFluxo == 'substituir')
      GetVisionElaboration(planId).then((res: any) => {
        if (res.data.length > 0) {
          setData(res.data);
        }
      })
  }, [GetVisionElaboration, planId, router.pathname, tipoFluxo]);

  React.useEffect(() => {
    if (router.query.id && router.pathname.split('/').includes("aprovacao_planos"))
      GetVisionElaboration(router.query.id).then((res: any) => {
        if (res.data.length > 0) {
          setData(res.data);
        }
      })
  }, [GetVisionElaboration, router.pathname, router.query.id]);

  React.useEffect(() => {
    const code = completCode.find(item => item.code == 'visao');

    if (code != undefined && code.skipped) {
      setValueVision('is_optional', false);
    }


    if (code != undefined && !code.skipped) {
      setValueVision('is_optional', true);
    }
  }, [completCode, setValueVision]);

  React.useEffect(() => {
    if (zustandSelectedCardItem.title != 'Plano Setorial') {
      setValueVision('is_optional', true)
    }
  }, [setValueVision, zustandSelectedCardItem.title])

  const routerPlanId = React.useMemo(() => Number(router.query.id), [router.query.id]);
  const { consultarOrganograma, salvarOrganograma, startOrganograma } = React.useMemo(() => new Organograma(routerPlanId), [routerPlanId]);
  const { insertItemById, findNewItemsWithId, addRootLayer } = useHierarchyPreparationOfPlans();
  const { startRelacionamento, consultarRelacionamento, atualizarRelacionamento } = React.useMemo(() => new RelacionamentoJsonService(routerPlanId), [routerPlanId]);

  const onSubmit: SubmitHandler<any> = async (data, event: any) => {
    if (disabeSubmit) return;
    const code = completCode.find(item => item.code == 'visao');

    setDisableSubmit(true);

    try {
      const { visao } = data;

      // Mapeia o array 'visao' e transforma os dados
      const jsonArray = visao.map((item: any) => {
        const { id, ...res } = item;
        return {
          ...res,
          ...(isNumber(id) ? { id } : {}),
          data: dayjs(item.data).format('YYYY-MM-DD') + 'T00:00:00',
        };
      });

      // Executa todas as requisições em paralelo e captura os resultados sem interromper o fluxo
      const responses = await Promise.allSettled(jsonArray.map((json: any) => VisionElaboration(json, router.query.id)));

      // Extrai apenas os valores que deram certo (fulfilled) e continua o fluxo
      const fulfilledResponses = responses
        .filter((response: any) => response.status === 'fulfilled')
        .map((response: any) => response.value);

      // Consulta o relacionamento após todas as requisições
      const responseRelacionamento = await consultarRelacionamento();
      let relacionamento = "";

      if (responseRelacionamento.length === 0) {
        relacionamento = responseRelacionamento;
      } else {
        relacionamento = JSON.parse(JSON.parse(responseRelacionamento));
      }

      if (Object.keys(relacionamento).length > 0 && !relacionamento.hasOwnProperty('visao')) {
        const updatedRelacionamento = addRootLayer(relacionamento, 'visao');
        startRelacionamento(JSON.stringify(updatedRelacionamento));
        await atualizarRelacionamento();
      }

      // Encontra novos itens com ID
      const newArray = findNewItemsWithId(visao, fulfilledResponses.map(resp => resp.data));

      // Processa os novos itens em paralelo, sem interromper o fluxo em caso de falhas
      await Promise.allSettled(newArray.map(async (item: any) => {
        const newItem = insertItemById({
          root: relacionamento,
          targetId: item.id,
          item: { id: item.id, relacionamento: {} },
          layerName: 'visao'
        });
        startRelacionamento(JSON.stringify(newItem));
        await atualizarRelacionamento();
      }));

      // Exibe o snackbar de sucesso
      toggleSuccessSnackbar();

      // Atualiza o plano e navega conforme a ação executada
      const plan = itemsMenu[findingMenuItemKey(zustandSelectedCardItem)] as Record<string, any>;
      let planId;

      if (event.nativeEvent.submitter.name === 'next_step') {
        setNavigable(true);
        planId = plan[selectedItemsMenu?.layer as string].find((item: any) => item.id === selectedItemsMenu?.id).id + 1;
        if (router.pathname.includes('epl')) {
          router.push('dashboard/aprovacao_planos/' + router.query.id + '/epl/preview')
        } else {
          router.push('/dashboard/elaboracao_planos/' + router.query.id);
        }
      } else {
        setNavigable(false);
        planId = plan[selectedItemsMenu?.layer as string].find((item: any) => item.id === selectedItemsMenu?.id).id;
        if (router.pathname.includes('epl')) {
          router.push('dashboard/aprovacao_planos/' + router.query.id + '/epl/preview')
        } else {
          router.push('/dashboard/elaboracao_planos/' + router.query.id);
        }
      }

      // Define o novo item no menu e busca progresso do plano
      setDefaultItemMenuId(planId);
      handleFetchProgressCodePlanId(router.query.id);

      // Aguarda 3 segundos e exibe novamente o snackbar de sucesso
      setTimeout(() => toggleSuccessSnackbar(), 3000);
    } catch (error) {
      // Em caso de erro, exibe o snackbar de erro
      toggleErrorSnackbar();
    } finally {
      // Independentemente de sucesso ou erro, reabilita o botão de submit
      setTimeout(() => setDisableSubmit(false), 3000);
    }


    if (code != undefined) return;

    try {
      const response = await consultarOrganograma();
      const organograma = response.Organograma;

      const cenario = completCode.find(item => item.code == 'sintese_cenario')

      let manager: any;

      if (cenario != undefined) {
        manager = 'Síntese Diagnóstico'
      } else {
        manager = 'Síntese Diagnóstico'
      }

      const newItem = {
        "name": "Visão",
        "position": "",
        "manager": manager,
        "tooltip": "",
        "subordinates": []
      }

      const newOrganograma = addItemToNodeOrganogram(organograma, manager, newItem);

      startOrganograma(newOrganograma);
      salvarOrganograma()

    } catch {
    }
  };

  const Fields = () => {
    if (isOptional == 'false' || isOptional == undefined) return null;
    if (Boolean(isOptional) == false || isOptional == undefined) return null;

    return (
      <>
        {fields.map((field: any, index: any) => {
          const randomId = Math.random();
          const data = watchVision(`visao.${index}.data`);
          const texto = watchVision(`visao.${index}.texto`);


          return (
            <React.Fragment key={field?.id || `${index}-${texto}`}>
              <Grid item xs={12}>
                <input
                  type="hidden"
                  defaultValue={field.id}
                  {...registerVision(`visao.${index}.id`, { value: field.id })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextFieldElaboration
                  rows={2}
                  label="Visão"
                  required={true}
                  multiline={true}
                  errors={errosVision}
                  defaultValue={texto}
                  commentTittle={texto}
                  register={registerVision}
                  formName="Visão"
                  name={`visao.${index}.texto`}
                  placeholder="Digite a visão do plano"
                />
                <div className="error_wrapper">
                  <span>{getMessageErrorByNameRef(errosVision, 'texto')}</span>
                </div>
              </Grid>
              <Grid item xs={5}>
                <DatePickerElaboration
                  required={true}
                  defaultValue={data}
                  errors={errosVision}
                  formName="Visão"
                  label="Prazo da visão"
                  control={controlVision as any}
                  commentTittle={texto}
                  name={`visao.${index}.data`}
                />
                <div className="error_wrapper">
                  <span>{getMessageErrorByNameRef(errosVision, 'data')}</span>
                </div>
              </Grid>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <div style={{ height: '1px', background: '#B4B4B4' }}></div>
              </Grid>
            </React.Fragment>
          );
        })}

      </>
    );
  };

  const CheckBoxField = () => {
    if (zustandSelectedCardItem.title == 'Plano de Estado' || zustandSelectedCardItem.title == 'Plano Institucional') return null;
    const opitional = () => {
      if (typeof isOptional == 'string') {
        return isOptional == 'true' ? 'true' : 'false';
      } else {
        return Boolean(isOptional) == true ? 'true' : 'false'
      }
    }
    return (
      <Grid item xs={12}>
        <RadioCheckedElaboration
          errors={errosVision}
          required={false}
          control={controlVision as any}
          name="is_optional"
          defaultValue={opitional()}
          data={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]}
          disabled={completCode?.find(item => item.code == 'visao') ? true : false}
          label="Este elemento é opcional para o tipo de plano selecionado. Deseja preencher a informação?"
        />
      </Grid>
    );
  };

  const ReplaceButton = () => {
    if (tipoFluxo != 'substituir') return null;
    return (
      <Grid item xs={4}>
        <button className="recicle_content_button" type='button' onClick={() => {
          if (data.length > 0) {
            setReutilizar(true);
          } else {
            setOpenDisclaimer(true);
          }
        }}>
          <p>Reutilizar conteúdo</p>
        </button>
      </Grid>
    );
  };

  const actionButton = () => {
    const optional = () => {
      if (typeof isOptional == 'string') {
        return isOptional == 'true' ? 'true' : 'false';
      } else {
        return Boolean(isOptional) == true ? 'true' : 'false'
      }
    }

    const optionalV2 = () => {
      if (typeof isOptional == 'string') {
        return isOptional == 'false' || isOptional == undefined;
      } else {
        return Boolean(isOptional) == false || isOptional == undefined;
      }
    }

    return (
      <ActionButtonElaborations disable={disabeSubmit} isOptional={optional()} layer_indicator={false} onClick={() => {
        if (optionalV2()) {
          setNavigable(true)

          router.push('/dashboard/elaboracao_planos/' + router.query.id);
          PostProgressCode({ code: 'visao', skipped: true }, router.query.id)
          const plan = itemsMenu[findingMenuItemKey(zustandSelectedCardItem)] as Record<string, any>;
          if (plan && plan[selectedItemsMenu?.layer as string]) {
            const planId = plan[selectedItemsMenu?.layer as string]
              .find((item: any) => item.id == selectedItemsMenu?.id).id + 1;
            setDefaultItemMenuId(planId);
          }
        }
      }} />
    )
  }

  if (loading) {
    return <LoadingBuffer />
  }

  return (
    <form onSubmit={handleSubmitVision(onSubmit)}>
      <Container>
        <Grid container spacing={2}>
          {CheckBoxField()}
          {ReplaceButton()}
          <Grid item xs={12}>
            <div style={{ height: '1px', background: '#B4B4B4' }}></div>
          </Grid>
          {Fields()}
          <Grid item xs={12}>
            {actionButton()}
          </Grid>
          <ReutilizarElaborationDialog
            title="Visão"
            reuseData={data}
            open={reutilizar}
            setOpen={() => setReutilizar(false)}
            setValue={(e) => {
              const data = e.map((item: any, index: any) => {
                setValueVision(`visao.${index}.texto`, item?.texto)
                setValueVision(`visao.${index}.data`, dayjs(item.data))
              });
            }}
          />
        </Grid>
        <DisclaimerReuseElaborationDialog open={openDisclaimer} setOpen={setOpenDisclaimer} />
      </Container>
    </form>
  )
}
